import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * Post-signup newsletter opt-in modal.
 * Mounts globally; renders only when:
 *   1. User is logged in
 *   2. user.newsletter_consent_at is null (never made a choice)
 *   3. localStorage 'sd_newsletter_modal_dismissed' is unset
 *
 * Action: subscribe (POSTs consent=true) or "no thanks" (sets localStorage flag,
 * doesn't touch backend — leaves consent_at null so the user can subscribe via
 * account settings later without needing to re-confirm).
 */
const DISMISS_KEY = 'sd_newsletter_modal_dismissed';

const NewsletterWelcomeModal: React.FC = () => {
  const { user, token, refreshProfile } = useAuth();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !token) return;
    if (user.newsletter_consent_at) return; // already chose
    if (typeof window !== 'undefined' && window.localStorage.getItem(DISMISS_KEY)) return;
    // Small delay so it doesn't pop the moment OAuth lands — feels less jarring.
    const t = setTimeout(() => setOpen(true), 800);
    return () => clearTimeout(t);
  }, [user, token]);

  const dismiss = () => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(DISMISS_KEY, '1');
    }
    setOpen(false);
  };

  const subscribe = async () => {
    if (submitting || !token) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/auth/me/newsletter-consent/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Token ${token}`,
        },
        body: JSON.stringify({ consent: true }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || data.error || 'Could not subscribe right now.');
      }
      // Mark dismissed and refresh profile so user.newsletter_consent_at flips.
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(DISMISS_KEY, '1');
      }
      await refreshProfile();
      setOpen(false);
    } catch (e: any) {
      setError(e?.message || 'Could not subscribe right now.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50" role="dialog" aria-modal="true" aria-labelledby="nl-welcome-title">
      <div className="max-w-md w-full bg-white rounded-xl shadow-xl p-6 sm:p-7">
        <div className="mb-4">
          <h2 id="nl-welcome-title" className="text-lg font-bold text-slate-900 mb-2">
            Welcome to SaveDelete
          </h2>
          <p className="text-sm text-slate-600 leading-relaxed">
            Get five hand-picked stories on AI, crypto, and tech every Monday. One email. No spam. Unsubscribe any time.
          </p>
        </div>
        {error && (
          <p className="text-sm text-red-700 mb-3" role="alert">{error}</p>
        )}
        <div className="flex flex-col sm:flex-row-reverse gap-2 sm:gap-3">
          <button
            type="button"
            onClick={subscribe}
            disabled={submitting}
            className="px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? 'Subscribing…' : 'Yes, subscribe me'}
          </button>
          <button
            type="button"
            onClick={dismiss}
            disabled={submitting}
            className="px-4 py-2.5 text-sm text-slate-600 hover:text-slate-900 rounded-lg hover:bg-slate-100"
          >
            No thanks
          </button>
        </div>
        <p className="text-xs text-slate-400 mt-4 text-center">
          You can change this anytime in account settings.
        </p>
      </div>
    </div>
  );
};

export default NewsletterWelcomeModal;
